import React, { FC } from 'react';
import { Box, FormControl, MultiSelect, Text, TextFieldAppearance, TextFieldSize } from '@bechtel/gds';
import { Facility, FacilityUnitRate } from '@bechtel/types';
import { NetworkStatus } from '@apollo/client';
import { ErrorMessage, Field, FormikProps } from 'formik';
import { get, map } from 'lodash';
import { useAppUser, ErrorRetry } from '@bechtel/client-common';
import { useProjectFacilityQuery } from '~/user-admin/hooks/use-facility';
import { FormikState } from '~/user-admin/users/form-state';
import { FieldError } from '../styled';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

const Item = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000">
      {i18n._(item.name)}
    </Text>
  );
};

interface FacilityListProps {
  formikProps: FormikProps<FormikState>;
}

export const FacilityList: FC<FacilityListProps> = ({ formikProps }) => {
  const { user } = useAppUser();
  const { setFieldValue, values } = formikProps;
  const {
    loading: facilityLoading,
    error: facilityError,
    data: facilityData,
    refetch: facilityRefetch,
    networkStatus: facilityNetworkStatus,
  } = useProjectFacilityQuery(user?.project?.id ? user.project.id : '');
  const facilityUnitRateList: FacilityUnitRate[] = get(facilityData, 'projectConfigById.facilityUnitRates', []);
  const facilityList: Facility[] = map(facilityUnitRateList, (facilityUnitRate) => facilityUnitRate.facility);
  return (
    <>
      {!facilityLoading && facilityError && (
        <ErrorRetry refetch={facilityRefetch}>
          <Trans>There was a problem loading facility list. Please click the button below to try again.</Trans>
        </ErrorRetry>
      )}
      {!facilityError && (
        <Field name="facilities">
          {({ field }) => {
            return (
              <Box flexDirection={{ sm: 'column' }} mb={{ sm: '500' }}>
                <FormControl style={{ flexGrow: 1 }} label={t`Facilities`}>
                  <MultiSelect
                    {...field}
                    items={facilityList}
                    itemComponent={Item}
                    selectedItems={values.facilities}
                    size={TextFieldSize.LARGE}
                    appearance={TextFieldAppearance.NORMAL}
                    displayKeyField="name"
                    loading={facilityLoading || facilityNetworkStatus === NetworkStatus.refetch}
                    onSelectedItemsChange={async (changes) => {
                      const { selectedItems } = changes;
                      setFieldValue('facilities', selectedItems);
                    }}
                  ></MultiSelect>
                </FormControl>
                <ErrorMessage name="facilities">
                  {(msg) => {
                    return (
                      <Box pt={{ sm: '400' }} pb={{ sm: '500' }}>
                        <FieldError>{msg}</FieldError>
                      </Box>
                    );
                  }}
                </ErrorMessage>
              </Box>
            );
          }}
        </Field>
      )}
    </>
  );
};

export default FacilityList;
